// Form state manager for comprehensive draft functionality

export const getCompleteFormState = (appState) => {
  return {
    // Current step
    activeStep: appState.activeStep,
    
    // Step 0 - Customer Details
    formData: appState.formData,
    entityIdType: appState.entityIdType,
    entityIdNumber: appState.entityIdNumber,
    entityIdError: appState.entityIdError,
    
    // Step 1 - Documents and Files
    files: {
      gstFile: appState.gstFile?.name || null,
      panFile: appState.panFile?.name || null,
      chequeFile: appState.chequeFile?.name || null,
      tanFile: appState.tanFile?.name || null,
      incFile: appState.incFile?.name || null,
      declarationFile: appState.declarationFile?.name || null,
      partnershipFile: appState.partnershipFile?.name || null,
      passbookFile: appState.passbookFile?.name || null,
      addressFile: appState.addressFile?.name || null,
      swiftFile: appState.swiftFile?.name || null,
      aadhaarFile: appState.aadhaarFile?.name || null,
      altDocFile: appState.altDocFile?.name || null,
      mandateFile: appState.mandateFile?.name || null,
    },
    
    // File links (S3 URLs)
    fileLinks: {
      gstFileLink: appState.gstFileLink,
      panFileLink: appState.panFileLink,
      chequeFileLink: appState.chequeFileLink,
      tanFileLink: appState.tanFileLink,
      incFileLink: appState.incFileLink,
      declarationFileLink: appState.declarationFileLink,
      partnershipFileLink: appState.partnershipFileLink,
      passbookFileLink: appState.passbookFileLink,
      addressFileLink: appState.addressFileLink,
      swiftFileLink: appState.swiftFileLink,
      aadhaarFileLink: appState.aadhaarFileLink,
      altDocFileLink: appState.altDocFileLink,
      mandateFileLink: appState.mandateFileLink,
    },
    
    // OCR extracted details
    extractedDetails: {
      gstDetails: appState.gstDetails,
      panDetails: appState.panDetails,
      chequeDetails: appState.chequeDetails,
      tanDetails: appState.tanDetails,
      aadhaarDetails: appState.aadhaarDetails,
    },
    
    // Manual entries for when OCR fails
    manualTanNumber: appState.manualTanNumber,
    
    // Bank details and cheque alternatives
    bankDetails: appState.bankDetails,
    hasNoCheque: appState.hasNoCheque,
    
    // Alternative document type
    altDocType: appState.altDocType,
    
    // Step 2 - Review and Additional Address
    additionalAddress: appState.additionalAddress,
    checked: appState.checked,
    
    // Step 3 - Additional Information
    additional: appState.additional,
    
    // Timestamps
    lastModified: new Date().toISOString(),
    leadId: appState.leadId
  };
};

export const applyFormState = (draftState, setters) => {
  const {
    setActiveStep,
    setFormData,
    setEntityIdType,
    setEntityIdNumber,
    setEntityIdError,
    
    // File setters - Note: We can't restore File objects, only track names
    setGstFileLink,
    setPanFileLink,
    setChequeFileLink,
    setTanFileLink,
    setIncFileLink,
    setDeclarationFileLink,
    setPartnershipFileLink,
    setPassbookFileLink,
    setAddressFileLink,
    setSwiftFileLink,
    setAadhaarFileLink,
    setAltDocFileLink,
    setMandateFileLink,
    
    // Details setters
    setGstDetails,
    setPanDetails,
    setChequeDetails,
    setTanDetails,
    setAadhaarDetails,
    
    // Manual entries
    setManualTanNumber,
    
    // Bank details
    setBankDetails,
    setHasNoCheque,
    
    // Alternative document
    setAltDocType,
    
    // Address
    setAdditionalAddress,
    setChecked,
    
    // Additional info
    setAdditional,
  } = setters;

  try {
    // Apply basic form data
    if (draftState.activeStep !== undefined) setActiveStep(draftState.activeStep);
    if (draftState.formData) setFormData(draftState.formData);
    if (draftState.entityIdType) setEntityIdType(draftState.entityIdType);
    if (draftState.entityIdNumber) setEntityIdNumber(draftState.entityIdNumber);
    if (draftState.entityIdError) setEntityIdError(draftState.entityIdError);
    
    // Apply file links
    if (draftState.fileLinks) {
      if (draftState.fileLinks.gstFileLink) setGstFileLink(draftState.fileLinks.gstFileLink);
      if (draftState.fileLinks.panFileLink) setPanFileLink(draftState.fileLinks.panFileLink);
      if (draftState.fileLinks.chequeFileLink) setChequeFileLink(draftState.fileLinks.chequeFileLink);
      if (draftState.fileLinks.tanFileLink) setTanFileLink(draftState.fileLinks.tanFileLink);
      if (draftState.fileLinks.incFileLink) setIncFileLink(draftState.fileLinks.incFileLink);
      if (draftState.fileLinks.declarationFileLink) setDeclarationFileLink(draftState.fileLinks.declarationFileLink);
      if (draftState.fileLinks.partnershipFileLink) setPartnershipFileLink(draftState.fileLinks.partnershipFileLink);
      if (draftState.fileLinks.passbookFileLink) setPassbookFileLink(draftState.fileLinks.passbookFileLink);
      if (draftState.fileLinks.addressFileLink) setAddressFileLink(draftState.fileLinks.addressFileLink);
      if (draftState.fileLinks.swiftFileLink) setSwiftFileLink(draftState.fileLinks.swiftFileLink);
      if (draftState.fileLinks.aadhaarFileLink) setAadhaarFileLink(draftState.fileLinks.aadhaarFileLink);
      if (draftState.fileLinks.altDocFileLink) setAltDocFileLink(draftState.fileLinks.altDocFileLink);
      if (draftState.fileLinks.mandateFileLink) setMandateFileLink(draftState.fileLinks.mandateFileLink);
    }
    
    // Apply extracted details
    if (draftState.extractedDetails) {
      if (draftState.extractedDetails.gstDetails) setGstDetails(draftState.extractedDetails.gstDetails);
      if (draftState.extractedDetails.panDetails) setPanDetails(draftState.extractedDetails.panDetails);
      if (draftState.extractedDetails.chequeDetails) setChequeDetails(draftState.extractedDetails.chequeDetails);
      if (draftState.extractedDetails.tanDetails) setTanDetails(draftState.extractedDetails.tanDetails);
      if (draftState.extractedDetails.aadhaarDetails) setAadhaarDetails(draftState.extractedDetails.aadhaarDetails);
    }
    
    // Apply manual entries
    if (draftState.manualTanNumber !== undefined) setManualTanNumber(draftState.manualTanNumber);
    
    // Apply bank details
    if (draftState.bankDetails) setBankDetails(draftState.bankDetails);
    if (draftState.hasNoCheque !== undefined) setHasNoCheque(draftState.hasNoCheque);
    
    // Apply alternative document
    if (draftState.altDocType) setAltDocType(draftState.altDocType);
    
    // Apply address details
    if (draftState.additionalAddress) setAdditionalAddress(draftState.additionalAddress);
    if (draftState.checked !== undefined) setChecked(draftState.checked);
    
    // Apply additional information
    if (draftState.additional) setAdditional(draftState.additional);
    
    return true;
  } catch (error) {
    console.error('Error applying form state:', error);
    return false;
  }
};

export const createFileStateIndicator = (files, fileLinks) => {
  // Create indicators for which files were uploaded (since we can't restore File objects)
  const indicators = {};
  
  Object.keys(files).forEach(key => {
    const fileName = files[key];
    const linkKey = key.replace('File', 'FileLink');
    const fileLink = fileLinks[linkKey];
    
    if (fileName && fileLink) {
      indicators[key] = {
        wasUploaded: true,
        fileName: fileName,
        fileLink: fileLink
      };
    }
  });
  
  return indicators;
};

export const shouldShowDraftDialog = (draftState) => {
  if (!draftState) return false;
  
  // Show dialog if user has made meaningful progress
  const hasFormData = draftState.formData && Object.values(draftState.formData).some(val => val);
  const hasFileLinks = draftState.fileLinks && Object.values(draftState.fileLinks).some(val => val);
  const hasAdditionalData = draftState.additional && Object.values(draftState.additional).some(val => val);
  
  return hasFormData || hasFileLinks || hasAdditionalData;
};